import { useState } from "react";
import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import { useUpdateInvoiceCustomerMutation } from "store/apis/invoices";
import { useToast } from "hooks/useToast";
import { CustomerAddress, GroupInvoiceCustomer } from "types/Models/invoice";
import { BackendError } from "types/utils/error";
import { CustomerAddressRow } from "./CustomerAddressRow";

export const AddCustomerAddressesModal = ({
  open,
  onClose,
  customers,
  groupId,
  entityId,
}: {
  open: boolean;
  onClose: () => void;
  customers: GroupInvoiceCustomer[];
  groupId: string;
  entityId: string;
}) => {
  const [selectedAddresses, setSelectedAddresses] = useState<
    Record<string, CustomerAddress>
  >({});
  const [updateInvoiceCustomer, { isLoading }] =
    useUpdateInvoiceCustomerMutation();
  const { alertToast, successToast } = useToast();

  const handleAddressSelect = (
    customerId: string,
    address: CustomerAddress | null
  ) => {
    setSelectedAddresses((prev) => {
      const next = { ...prev };
      if (address) {
        next[customerId] = address;
      } else {
        delete next[customerId];
      }
      return next;
    });
  };

  const allCustomersSelected =
    customers.length > 0 &&
    customers.every((customer) => selectedAddresses[customer.uuid]);

  const handleSaveDetails = async () => {
    try {
      await Promise.all(
        customers.map((customer) =>
          updateInvoiceCustomer({
            groupId,
            entityId,
            customerId: customer.uuid,
            payload: {
              billing_address_id: selectedAddresses[customer.uuid].uuid,
              currency: customer.currency,
            },
          }).unwrap()
        )
      );
      successToast({ message: "Customer details have been updated!" });
      onClose();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ??
          "Something went wrong",
      });
    }
  };

  return (
    <Modal.Root open={open} onOpenChange={onClose}>
      <Modal.Content
        useCustomOverlay
        size="xl"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
      >
        <Modal.Header>
          <Modal.Title>Add customer details</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          <p className="t-text-text-30 t-text-body-sm t-mb-4">
            Add the billing address for the following customers to accurately
            track sales tax thresholds.
          </p>
          <div className="t-flex t-flex-col">
            <div className="t-grid t-grid-cols-2 t-gap-4 t-pb-2 t-border-0 t-border-b t-border-solid t-border-neutral-0">
              <p className="t-m-0 t-text-body-sm t-text-text-30">
                Customer name
              </p>
              <p className="t-m-0 t-text-body-sm t-text-text-30">
                Billing address
              </p>
            </div>
            {customers.map((customer) => (
              <CustomerAddressRow
                key={customer.uuid}
                customerId={customer.uuid}
                customerName={customer.name}
                groupId={groupId}
                entityId={entityId}
                onAddressSelect={handleAddressSelect}
              />
            ))}
          </div>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            customType="primary"
            onClick={handleSaveDetails}
            disabled={!allCustomersSelected || isLoading}
            isLoading={isLoading}
          >
            Save details
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
